import React, { useEffect, useState } from 'react';
import { Table, Tag, Typography, Button, Alert, Card, Avatar, Form, Input, Modal, Popconfirm, Space, message } from 'antd';
import { PlusOutlined, UserOutlined, CheckCircleOutlined, StopOutlined } from '@ant-design/icons';
import {
  activateUser, createUser, deactivateUser, getUserAccess, getUsers, setUserPassword, updateUser,
} from '../services/api';
import { getApiError } from '../utils/apiError';

const { Title, Text } = Typography;

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [canManage, setCanManage] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);   // a user, or {} for a new one
  const [passwordFor, setPasswordFor] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  useEffect(() => {
    loadUsers();
    getUserAccess().then((res) => setCanManage(res.data.can_manage_users)).catch(() => setCanManage(false));
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (err) {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (user: any) => {
    form.resetFields();
    if (user.id) form.setFieldsValue(user);
    setEditing(user);
  };

  const saveUser = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing?.id) {
        await updateUser(editing.id, { full_name: values.full_name, email: values.email, mobile: values.mobile || null });
        message.success('User updated.');
      } else {
        await createUser({ ...values, mobile: values.mobile || null });
        message.success('User created.');
      }
      setEditing(null);
      loadUsers();
    } catch (err) {
      message.error(getApiError(err, 'Could not save the user.'));
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    const values = await passwordForm.validateFields();
    setSaving(true);
    try {
      await setUserPassword(passwordFor.id, values.password);
      message.success('Password changed.');
      setPasswordFor(null);
    } catch (err) {
      message.error(getApiError(err, 'Could not change the password.'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (user: any) => {
    try {
      if (user.is_active) await deactivateUser(user.id); else await activateUser(user.id);
      message.success(user.is_active ? 'User deactivated.' : 'User reactivated.');
      loadUsers();
    } catch (err) {
      message.error(getApiError(err, 'Could not change the user.'));
    }
  };

  const columns = [
    {
      title: 'User',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (name: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar
            style={{ background: '#1B2A4A' }}
            icon={<UserOutlined />}
          >
            {name?.charAt(0)}
          </Avatar>
          <div>
            <Text strong>{name}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>{record.username}</Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      key: 'mobile',
      render: (mobile: string) => mobile || '—',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => active
        ? <Tag icon={<CheckCircleOutlined />} color="green">Active</Tag>
        : <Tag icon={<StopOutlined />} color="red">Inactive</Tag>,
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (date: string) => date
        ? new Date(date).toLocaleString('en-IN')
        : 'Never',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('en-IN'),
    },
    ...(canManage ? [{
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" onClick={() => openEditor(record)}>Edit</Button>
          <Button size="small" onClick={() => { passwordForm.resetFields(); setPasswordFor(record); }}>Password</Button>
          <Popconfirm
            title={record.is_active ? 'Deactivate this user?' : 'Reactivate this user?'}
            description={record.is_active ? 'They can no longer log in. Their past records stay.' : undefined}
            okText="Yes" onConfirm={() => toggleActive(record)}
          >
            <Button size="small" danger={record.is_active}>{record.is_active ? 'Deactivate' : 'Reactivate'}</Button>
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, color: '#1B2A4A' }}>
          User Management
        </Title>
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} style={{ background: '#1B2A4A' }} onClick={() => openEditor({})}>
            Add User
          </Button>
        )}
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

      <Card style={{ borderRadius: 12 }}>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No users found.' }}
        />
      </Card>

      <Modal
        title={editing?.id ? 'Edit user' : 'Add user'}
        open={!!editing} onCancel={() => setEditing(null)} onOk={saveUser}
        okText="Save" confirmLoading={saving} destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="full_name" label="Full name" rules={[{ required: true, message: 'Enter the name' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="username" label="Username" rules={[{ required: !editing?.id, message: 'Enter a username' }]}>
            <Input disabled={!!editing?.id} />
          </Form.Item>
          <Form.Item
            name="email" label="Email"
            rules={[{ required: true, message: 'Enter the email' }, { type: 'email', message: 'Not a valid email' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="mobile" label="Mobile (optional)"><Input /></Form.Item>
          {!editing?.id && (
            <Form.Item
              name="password" label="Password"
              rules={[{ required: true, min: 8, message: 'At least 8 characters' }]}
            >
              <Input.Password />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title={passwordFor ? `New password for ${passwordFor.full_name}` : ''}
        open={!!passwordFor} onCancel={() => setPasswordFor(null)} onOk={savePassword}
        okText="Change password" confirmLoading={saving} destroyOnHidden
      >
        <Form form={passwordForm} layout="vertical">
          <Form.Item name="password" label="New password" rules={[{ required: true, min: 8, message: 'At least 8 characters' }]}>
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersPage;